import { motion, type HTMLMotionProps } from 'motion/react'
import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost'
type Size = 'sm' | 'md' | 'lg'

const variantCls: Record<Variant, string> = {
  primary:   'bg-[#0F70B7] text-white hover:bg-[#0d5c96] focus-visible:ring-[#0F70B7]',
  secondary: 'bg-[#EE7108] text-white hover:bg-[#d66507] focus-visible:ring-[#EE7108]',
  outline:   'border-2 border-[#0F70B7] text-[#0F70B7] hover:bg-jepg-blue-light focus-visible:ring-[#0F70B7]',
  ghost:     'text-[#0F70B7] hover:text-[#0d5c96] hover:bg-jepg-blue-light focus-visible:ring-[#0F70B7]',
}

const sizeCls: Record<Size, string> = {
  sm: 'px-4 py-2 text-sm gap-1.5',
  md: 'px-6 py-2.5 text-base gap-2',
  lg: 'px-8 py-4 text-base gap-2',
}

type BtnOwnProps = {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: ReactNode
  iconRight?: ReactNode
  children?: ReactNode
}

type BtnProps = BtnOwnProps & Omit<HTMLMotionProps<'button'>, keyof BtnOwnProps>

export function Btn({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconRight,
  children,
  disabled,
  className = '',
  ...props
}: BtnProps) {
  const off = disabled || loading

  return (
    <motion.button
      whileHover={off ? {} : { scale: 1.025 }}
      whileTap={off ? {} : { scale: 0.965 }}
      transition={{ type: 'spring', stiffness: 420, damping: 22 }}
      disabled={off}
      className={`
        inline-flex items-center justify-center rounded-lg font-medium
        transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
        disabled:opacity-60 disabled:cursor-not-allowed shadow-sm
        ${variantCls[variant]} ${sizeCls[size]} ${className}
      `.replace(/\s+/g, ' ').trim()}
      {...props}
    >
      {loading
        ? <Loader2 size={16} className="animate-spin" />
        : icon ?? null}
      {children}
      {!loading && (iconRight ?? null)}
    </motion.button>
  )
}
